import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { supabase } from '../utils/supabase-client';
import { projectId } from '../../../utils/supabase/info';

export const JWTDebugger = () => {
  const [results, setResults] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const testJWT = async () => {
    try {
      setLoading(true);
      setResults(null);

      console.log('🧪 JWT Debugger: Starting test...');

      // Step 1: Get current session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        setResults({ error: 'Failed to get session', details: sessionError.message });
        return;
      }

      if (!session?.access_token) {
        setResults({ error: 'No active session. Please sign in first.' });
        return;
      }

      const token = session.access_token;
      console.log('🔐 Token obtained, length:', token.length);
      
      const tokenPreview = `${token.substring(0, 20)}...${token.substring(token.length - 20)}`;
      console.log('🔐 Token preview:', tokenPreview);

      // Step 2: Test the JWT debug endpoint on backend
      const apiUrl = `https://${projectId}.supabase.co/functions/v1/make-server-684c7e75/debug/test-jwt`;
      console.log('📡 Calling debug endpoint:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Supabase-Token': token,
          'Content-Type': 'application/json'
        }
      });

      console.log('📡 Response status:', response.status);
      console.log('📡 Response ok:', response.ok);

      const result = await response.json();
      console.log('📡 Response data:', result);

      setResults({
        success: response.ok,
        status: response.status,
        data: result,
        tokenPreview,
        sessionInfo: {
          user: session.user?.email,
          userId: session.user?.id,
          expiresAt: session.expires_at ? new Date(session.expires_at * 1000).toISOString() : 'unknown'
        }
      });

    } catch (error: any) {
      console.error('❌ JWT Debugger error:', error);
      setResults({ 
        error: 'Test failed', 
        details: error.message 
      });
    } finally {
      setLoading(false);
    }
  };

  const testConfigEndpoint = async () => {
    try {
      setLoading(true);
      setResults(null);

      const apiUrl = `https://${projectId}.supabase.co/functions/v1/make-server-684c7e75/debug-config`;
      console.log('📡 Calling config debug endpoint:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log('📡 Config response status:', response.status);
      console.log('📡 Config response ok:', response.ok);
      
      const result = await response.json();
      
      console.log('📡 Config response:', result);
      setResults({ 
        configCheck: result,
        responseStatus: response.status,
        responseOk: response.ok
      });

    } catch (error: any) {
      console.error('❌ Config check error:', error);
      setResults({ error: 'Config check failed', details: error.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-6 space-y-4">
      <div>
        <h3 className="text-lg font-semibold mb-2">JWT Debug Tool</h3>
        <p className="text-sm text-gray-600 mb-4">
          Use this tool to diagnose JWT authentication issues between frontend and backend.
        </p>
      </div>

      <div className="flex gap-2">
        <Button 
          onClick={testJWT} 
          disabled={loading}
          variant="default"
        >
          {loading ? 'Testing...' : 'Test JWT Validation'}
        </Button>
        
        <Button 
          onClick={testConfigEndpoint} 
          disabled={loading}
          variant="outline"
        >
          Check Backend Config
        </Button>
      </div>

      {results && (
        <div className="mt-4">
          <h4 className="font-semibold mb-2">Results:</h4>
          <pre className="bg-gray-100 p-4 rounded overflow-auto max-h-[600px] text-xs font-mono">
            {JSON.stringify(results, null, 2)}
          </pre>

          {results.success && results.data?.steps && (
            <div className="mt-4 space-y-2">
              <div className="font-semibold text-lg">✅ JWT Debug Summary:</div>
              <div className="space-y-2 text-sm">
                <div className="p-3 bg-gray-50 rounded">
                  <strong>User ID:</strong> {results.data.steps.step4_manualDecode?.userId}
                </div>
                <div className="p-3 bg-gray-50 rounded">
                  <strong>Token Status:</strong> {results.data.steps.step4_manualDecode?.expired ? '❌ Expired' : '✅ Valid'}
                </div>
                <div className="p-3 bg-gray-50 rounded">
                  <strong>Expires At:</strong> {results.data.steps.step4_manualDecode?.expiresAt}
                </div>
                <div className={`p-3 rounded ${results.data.steps.step4_manualDecode?.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                  <strong>Manual JWT Decode:</strong> {results.data.steps.step4_manualDecode?.success ? '✅ Working' : '❌ Failed'}
                </div>
                <div className={`p-3 rounded ${results.data.steps.step5_supabaseAnonValidation?.success ? 'bg-green-50 text-green-800' : 'bg-orange-50 text-orange-800'}`}>
                  <strong>Supabase Validation:</strong> {results.data.steps.step5_supabaseAnonValidation?.success ? '✅ Working' : '⚠️ Failed'}
                  {results.data.steps.step5_supabaseAnonValidation?.error && (
                    <div className="text-xs mt-1">Error: {results.data.steps.step5_supabaseAnonValidation.error}</div>
                  )}
                </div>
                {results.data.recommendation && (
                  <div className="mt-3 p-4 bg-blue-50 border-2 border-blue-300 rounded">
                    <strong className="block mb-2">💡 Recommendation:</strong>
                    <div className="text-sm">{results.data.recommendation}</div>
                  </div>
                )}
                {results.data.configInfo && (
                  <div className="mt-3 p-4 bg-gray-50 border border-gray-300 rounded">
                    <strong className="block mb-2">🔧 Backend Configuration:</strong>
                    <div className="text-xs space-y-1">
                      <div>Project ID Match: {results.data.configInfo.match?.projectIdMatch ? '✅' : '❌'}</div>
                      <div>Anon Key Match: {results.data.configInfo.match?.anonKeyMatch ? '✅' : '❌'}</div>
                      <div>Using Fallback: {results.data.configInfo.actual?.usingFallback ? 'Yes ⚠️' : 'No ✅'}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {results.error && (
            <div className="mt-4 p-4 bg-red-50 border-2 border-red-300 rounded text-red-900">
              <strong className="text-lg block mb-2">❌ Error:</strong>
              <div className="mb-2">{results.error}</div>
              {results.details && (
                <div className="text-sm mt-2 p-2 bg-red-100 rounded">
                  <strong>Details:</strong> {results.details}
                </div>
              )}
            </div>
          )}
          
          {!results.success && !results.error && results.data?.error && (
            <div className="mt-4 p-4 bg-orange-50 border-2 border-orange-300 rounded text-orange-900">
              <strong className="text-lg block mb-2">⚠️ Test Issue:</strong>
              <div className="mb-2">{results.data.error}</div>
              {results.data.note && (
                <div className="text-sm mt-2 p-2 bg-orange-100 rounded">
                  {results.data.note}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  );
};